// SessionInviteShareModal.jsx - Share sheet for session invites
import React, { useCallback, useRef } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import { analyticsService } from "../../services/analytics";
import logger from "../../utils/logger";

/**
 * SessionInviteShareModal
 *
 * Shown after a session is saved when the inviter flagged non-user participants.
 * Provides share via native share sheet, WhatsApp, SMS, copy link, and a skip option.
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {string}   props.token
 * @param {string}   props.inviteUrl
 * @param {string}   props.gameName
 * @param {string}   props.guestName
 * @param {string}   props.inviterName
 * @param {number}   props.stepCurrent  - 1-based index of this invite in the queue
 * @param {number}   props.stepTotal    - total number of invites in this session
 * @param {Function} props.onClose  - called when user shares or skips (advances queue)
 */
function SessionInviteShareModal({
  isOpen,
  token,
  inviteUrl,
  gameName,
  guestName,
  inviterName,
  stepCurrent = 1,
  stepTotal = 1,
  onClose,
}) {
  const copyTimerRef = useRef(null);
  const [copied, setCopied] = React.useState(false);

  const shareMessage = `${inviterName} played ${gameName} with you on Pista! Claim your session and see the stats: ${inviteUrl}`;

  const handleNativeShare = useCallback(async () => {
    try {
      if (navigator?.share) {
        await navigator.share({
          title: `${inviterName} invited you to Pista`,
          text: shareMessage,
          url: inviteUrl,
        });
        analyticsService.track("session_invite_sent", { token, channel: "native_share" });
        onClose();
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        logger.warn("Native share failed:", err);
      }
    }
  }, [shareMessage, inviteUrl, inviterName, token, onClose]);

  const handleWhatsApp = useCallback(() => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    analyticsService.track("session_invite_sent", { token, channel: "whatsapp" });
    onClose();
  }, [shareMessage, token, onClose]);

  const handleSMS = useCallback(() => {
    const url = `sms:?&body=${encodeURIComponent(shareMessage)}`;
    window.location.href = url;
    analyticsService.track("session_invite_sent", { token, channel: "sms" });
    onClose();
  }, [shareMessage, token, onClose]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      analyticsService.track("session_invite_sent", { token, channel: "copy_link" });
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      logger.warn("Clipboard copy failed:", err);
    }
  }, [inviteUrl, token, onClose]);

  const handleSkip = useCallback(async () => {
    analyticsService.track("session_invite_skipped", { token });
    try {
      await httpRequest(`${API_BASE}/invites/session/${token}/skip`, {
        method: "POST",
        headers: { ...authService.getAuthHeaders(), "Content-Type": "application/json" },
      });
    } catch (err) {
      logger.warn("Failed to mark invite as skipped:", err);
    }
    onClose();
  }, [token, onClose]);

  React.useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div
      className="safe-area-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.5)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-modal="true"
      role="dialog"
      aria-label={`Invite ${guestName} to Pista`}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "var(--bg-primary, #fff)",
          borderRadius: "16px 16px 0 0",
          padding: "1.5rem 1.5rem 2rem",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "1rem" }}>
          {stepTotal > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <div style={{ display: "flex", gap: "4px" }}>
                {Array.from({ length: stepTotal }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      width: "20px",
                      height: "4px",
                      borderRadius: "2px",
                      backgroundColor: i < stepCurrent
                        ? "var(--pista-button-bg, #4caf50)"
                        : "var(--border-color, #ddd)",
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: "0.75rem", color: "var(--text-secondary, #888)", fontWeight: "600" }}>
                {stepCurrent} of {stepTotal}
              </span>
            </div>
          )}
          <h3 style={{ margin: "0 0 0.4rem", fontSize: "1.1rem", fontWeight: "700", color: "var(--text-primary, #333)" }}>
            Share invite link
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            <span style={{
              display: "inline-block",
              backgroundColor: "var(--pista-button-bg, #4caf50)",
              color: "var(--pista-button-text, white)",
              fontWeight: "700",
              fontSize: "0.95rem",
              padding: "0.15rem 0.6rem",
              borderRadius: "20px",
            }}>
              {guestName}
            </span>
            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary, #666)" }}>
              can claim the <strong>{gameName}</strong> session.
            </span>
          </div>
        </div>

        {/* Invite URL preview */}
        <div
          style={{
            backgroundColor: "var(--bg-secondary, #f5f5f5)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "8px",
            padding: "0.6rem 0.75rem",
            fontSize: "0.78rem",
            color: "var(--text-secondary, #666)",
            marginBottom: "1.25rem",
            wordBreak: "break-all",
          }}
        >
          {inviteUrl}
        </div>

        {/* Share buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {hasNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              style={primaryBtnStyle}
            >
              📤 Share via…
            </button>
          )}
          <button type="button" onClick={handleWhatsApp} style={secondaryBtnStyle}>
            💬 Send on WhatsApp
          </button>
          <button type="button" onClick={handleSMS} style={secondaryBtnStyle}>
            📱 Send as SMS
          </button>
          <button type="button" onClick={handleCopyLink} style={secondaryBtnStyle}>
            {copied ? "✓ Copied!" : "🔗 Copy link"}
          </button>
        </div>

        {/* Skip */}
        <button
          type="button"
          onClick={handleSkip}
          style={{
            marginTop: "1.25rem",
            width: "100%",
            background: "none",
            border: "none",
            color: "var(--text-secondary, #888)",
            fontSize: "0.85rem",
            cursor: "pointer",
            padding: "0.25rem",
          }}
        >
          {stepTotal > 1 && stepCurrent < stepTotal ? `Skip ${guestName}, go to next` : "Skip for now"}
        </button>
      </div>
    </div>
  );
}

const primaryBtnStyle = {
  width: "100%",
  padding: "0.75rem",
  backgroundColor: "var(--pista-button-bg, #4caf50)",
  color: "var(--pista-button-text, white)",
  border: "none",
  borderRadius: "8px",
  fontSize: "0.95rem",
  fontWeight: "600",
  cursor: "pointer",
};

const secondaryBtnStyle = {
  width: "100%",
  padding: "0.75rem",
  backgroundColor: "var(--bg-secondary, #f5f5f5)",
  color: "var(--text-primary, #333)",
  border: "1px solid var(--border-color, #ddd)",
  borderRadius: "8px",
  fontSize: "0.95rem",
  fontWeight: "500",
  cursor: "pointer",
};

export default SessionInviteShareModal;
